import React, { useState, useEffect } from 'react';

// PREVIEW BUILD ONLY — single self-contained file so it renders in chat.
// Differences from your deploy file: the COMPANIES array is stubbed (it is computed
// into `stats` but never displayed, so it does not affect what you see), and the one
// advisor headshot is a placeholder avatar instead of an inlined photo.

const FORMSPREE_CONFIG = {
  allianceForm: 'maqlgnye',
  researcherForm: 'xreowbgn',
  newsletterForm: 'mbdpvllr',
  outreachForm: 'xdapryen',
};

const submitToFormspree = async (formId, data) => {
  if (formId.startsWith('YOUR_')) {
    console.log('Form submission (Formspree not configured):', data);
    return { success: true, demo: true };
  }
  try {
    const response = await fetch(`https://formspree.io/f/${formId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return { success: response.ok };
  } catch (error) {
    console.error('Form submission error:', error);
    return { success: false, error };
  }
};

// Stubbed for preview (full list lives in your deploy file; it is not rendered).
const COMPANIES = [];

// Sow banner. In production set this to "/sow.jpg" and drop the file in public/.
const SOW_IMG = "/sow.jpg";

const useScrollReveal = (threshold = 0.1) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = React.useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, isVisible];
};

const RevealSection = ({ children, className = '', delay = 0 }) => {
  const [ref, isVisible] = useScrollReveal(0.15);
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
        transition: `all 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${delay}s`
      }}
    >
      {children}
    </div>
  );
};

const Logo = ({ size = 'md', variant = 'symbol', className = '' }) => {
  const sizes = { sm: 'text-2xl', md: 'text-4xl', lg: 'text-6xl', xl: 'text-8xl', hero: 'text-9xl' };
  if (variant === 'symbol') {
    return (
      <span className={`font-light tracking-tighter text-amber-400 ${sizes[size]} ${className} inline-flex items-center`} style={{ fontFamily: 'system-ui, -apple-system, sans-serif', lineHeight: 1 }}>
        <span>(</span><span style={{ position: 'relative', top: '0.08em' }}>0</span><span>)</span>
      </span>
    );
  }
  return (
    <span className={`text-amber-400 ${sizes[size]} ${className} inline-flex items-center`} style={{ lineHeight: 1 }}>
      <span className="inline-flex items-center tracking-tighter" style={{ fontFamily: 'system-ui, -apple-system, sans-serif', fontWeight: 400 }}>
        <span>(</span><span style={{ position: 'relative', top: '0.08em' }}>0</span><span>)</span>
      </span>
      <span style={{ fontFamily: '"JetBrains Mono", ui-monospace, monospace', fontWeight: 400, position: 'relative', top: '0.14em' }}>loosh</span>
    </span>
  );
};

const Navigation = ({ activeSection, onNavigate, onHome }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const navItems = [
    { id: 'home', label: 'Home' },
    { id: 'about', label: 'The Problem' },
    { id: 'science', label: 'Science' },
    { id: 'economics', label: 'Economics' },
    { id: 'roadmap', label: 'Status' },
  ];
  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'bg-zinc-950 shadow-2xl' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <button onClick={onHome} className="flex items-center gap-2 group" aria-label="Back to 0loosh.org home">
          <Logo size="sm" variant="wordmark" />
        </button>
        <div className="hidden lg:flex items-center gap-8">
          {navItems.map((item) => (
            <button key={item.id} onClick={() => onNavigate(item.id)}
              className={`text-sm tracking-wide transition-all duration-300 ${activeSection === item.id ? 'text-amber-400' : 'text-zinc-400 hover:text-white'}`}>
              {item.label}
            </button>
          ))}
        </div>
        <button className="lg:hidden text-white p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
          <div className="w-6 h-5 flex flex-col justify-between">
            <span className={`w-full h-0.5 bg-white transition-all duration-300 ${mobileMenuOpen ? 'rotate-45 translate-y-2' : ''}`} />
            <span className={`w-full h-0.5 bg-white transition-all duration-300 ${mobileMenuOpen ? 'opacity-0' : ''}`} />
            <span className={`w-full h-0.5 bg-white transition-all duration-300 ${mobileMenuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
          </div>
        </button>
      </div>
      {mobileMenuOpen && (
        <div className="lg:hidden bg-zinc-950 border-t border-zinc-800">
          {navItems.map((item) => (
            <button key={item.id} onClick={() => { onNavigate(item.id); setMobileMenuOpen(false); }}
              className="block w-full text-left px-6 py-4 text-zinc-300 hover:text-amber-400 hover:bg-zinc-900 transition-all">
              {item.label}
            </button>
          ))}
        </div>
      )}
    </nav>
  );
};

const Hero = ({ onNavigate }) => {
  return (
    <section id="home" style={{ minHeight: '82vh' }} className="relative flex items-center justify-center overflow-hidden">
      <style>{`
        @keyframes heartbeat { 0%{transform:scale(1)} 14%{transform:scale(1.04)} 28%{transform:scale(1)} 42%{transform:scale(1.02)} 70%{transform:scale(1)} 100%{transform:scale(1)} }
        @keyframes ringPulse { 0%{transform:scale(0.8);opacity:0.6} 100%{transform:scale(2.8);opacity:0} }
        @keyframes glowBreath { 0%,100%{opacity:0.15} 50%{opacity:0.35} }
        .hero-symbol { animation: heartbeat 2.4s ease-in-out infinite; }
        .hero-ring-1 { animation: ringPulse 2.4s ease-out infinite; }
        .hero-ring-2 { animation: ringPulse 2.4s ease-out infinite 0.3s; }
        .hero-glow { animation: glowBreath 2.4s ease-in-out infinite; }
      `}</style>
      <div className="absolute inset-0 bg-zinc-950" />
      <div className="absolute inset-0 bg-gradient-to-b from-zinc-950 via-zinc-900 to-zinc-950" />
      <div className="hero-glow absolute" style={{ top: '50%', left: '50%', transform: 'translate(-50%, -60%)', width: '600px', height: '600px', background: 'radial-gradient(circle, rgba(251,191,36,0.18) 0%, transparent 70%)', pointerEvents: 'none' }} />
      <div className="relative z-10 max-w-5xl mx-auto px-6 pt-16 md:pt-24 text-center">
        <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
          The End of Suffering<br />in Factory Farms
        </h1>
        <p className="text-lg md:text-xl text-zinc-300 max-w-3xl mx-auto mb-8 leading-relaxed">
          The first step is to engineer livestock that are <span className="text-white font-semibold">biologically incapable of suffering</span>. Not sedated or numbed, but without the molecular machinery that produces pain, fear, or distress. The aim past that is positive welfare: animals living in gradients of bliss.
        </p>
        <p className="text-zinc-500 text-sm mb-8">
          An initiative by{' '}
          <a href="https://www.0loosh.org/" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300 transition-colors">0loosh.org</a>
          {', launched April 2026'}
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button onClick={() => onNavigate('alliance')} className="px-8 py-4 bg-amber-400 text-zinc-950 font-semibold rounded-lg hover:bg-amber-300 transition-all duration-300 hover:scale-105">
            Join Industry Alliance
          </button>
          <button onClick={() => onNavigate('economics')} className="px-8 py-4 border-2 border-zinc-600 text-white font-semibold rounded-lg hover:border-amber-400 hover:text-amber-400 transition-all duration-300">
            View Economic Case
          </button>
        </div>
      </div>
    </section>
  );
};

const About = () => (
  <section id="about" className="pt-4 pb-24 bg-zinc-950 relative">
    <div className="max-w-5xl mx-auto px-6">
      <RevealSection>
        <figure className="mb-16">
          <div className="relative overflow-hidden">
            <img
              src={SOW_IMG}
              alt="A breeding sow confined in a gestation crate"
              className="w-full block"
              style={{ height: '180px', objectFit: 'cover', objectPosition: 'center 45%', maskImage: 'linear-gradient(to bottom, transparent 0%, #000 22%, #000 78%, transparent 100%)', WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, #000 22%, #000 78%, transparent 100%)' }}
            />
          </div>
        </figure>
      </RevealSection>
      <RevealSection delay={0.05}>
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 mb-8">
          <p className="text-zinc-300 leading-relaxed mb-4"><span className="text-white font-semibold">Pigs</span> confined in gestation crates develop stereotypic behaviors: repetitive, purposeless actions that are the animal cognition equivalent of psychiatric breakdown. They bite the tails of penmates or gnaw cage bars until bloody. The industry's response is tail docking, amputating piglets' tails at birth.</p>
          <p className="text-zinc-300 leading-relaxed mb-4"><span className="text-white font-semibold">Chickens</span> in battery cages exhibit feather pecking that escalates to cannibalism. The industry response is beak trimming, slicing the tip of the beak with a heated blade. They are kept under near-constant artificial light to maximize feeding, disrupting their circadian biology.</p>
          <p className="text-zinc-300 leading-relaxed mb-6"><span className="text-white font-semibold">Dairy calves</span> are separated from their mothers within 24 hours. Cows bellow for days. When animals are subjected to aversive conditions over which they have no control, they develop a behavioral syndrome functionally equivalent to clinical depression. The clinical term is <span className="text-amber-400 font-semibold">learned helplessness</span>. Factory farming is learned helplessness at industrial scale.</p>
          <p className="text-white text-lg leading-relaxed">More than 80 billion land animals are raised this way every year, and over a trillion fish. Eighty billion individual nervous systems. Each one matters.</p>
        </div>
      </RevealSection>
      <RevealSection delay={0.1}>
        <div className="bg-zinc-950 border border-amber-400 rounded-2xl p-8 mb-8">
          <p className="text-zinc-200 leading-relaxed mb-4"><span className="text-white font-semibold">The aim is positive welfare:</span> animals living in gradients of bliss. Abolishing suffering is the first and most urgent step, not the summit. You cannot credibly build positive valence on top of an animal still held in chronic fear and pain, and the floor is where the moral emergency is. So this is a sequence, floor first and then ceiling, raised on the same biology and proven at each stage.</p>
          <p className="text-zinc-300 leading-relaxed">Suffering and sensation are separable. The goal is never to delete an animal's ability to detect injury. It is to keep the protective signal, remove the suffering the signal is wrapped in, and then lift the baseline toward contentment and positive feeling. That sequence, decoupling affect from nociception and then raising valence, is the whole program, and the first step has a living existence proof.</p>
        </div>
      </RevealSection>
    </div>
  </section>
);

const Science = () => (
  <section id="science" className="py-24 bg-zinc-900 relative">
    <div className="max-w-5xl mx-auto px-6 relative">
      <RevealSection>
        <div className="text-center mb-16">
          <span className="text-amber-400 text-sm font-semibold tracking-widest uppercase mb-4 block">The Proposal</span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">A Germline Gene-Editing Approach</h2>
          <div className="w-24 h-1 bg-amber-400 mx-auto" />
        </div>
      </RevealSection>

      <RevealSection delay={0.1}>
        <div className="bg-zinc-950 border border-zinc-800 rounded-2xl p-8 mb-8">
          <h3 className="text-xl font-bold text-white mb-4">Jo Cameron, the existence proof</h3>
          <p className="text-zinc-300 leading-relaxed mb-4">Jo Cameron is a Scottish woman, 78 as of 2026, who has never experienced anxiety, cannot feel fear, and feels almost no pain, yet lives a healthy life with elevated mood and accelerated wound healing. She still feels joy, love, curiosity, and connection. She is the existence proof that a biological being can have fear, anxiety, and suffering switched off while awareness, mood, and the capacity for a full life remain intact.</p>
          <p className="text-zinc-300 leading-relaxed mb-4">Her phenotype traces to two changes: a mutation in <span className="font-mono text-amber-400">FAAH</span> and a deletion in <span className="font-mono text-amber-400">FAAH-OUT</span>, a regulatory region reported in 2019, with a wider molecular cascade mapped in 2023.</p>
          <p className="text-zinc-300 leading-relaxed"><span className="text-amber-400 font-semibold">But Jo Cameron is a clue, not a template.</span> FAAH-OUT is a human regulatory element and does not port cleanly to other species, and endocannabinoid biology differs across pigs, cattle, chickens, and fish. ACKR3, which appears in her downstream cascade, is not a safe edit: global germline ACKR3 knockout is perinatally lethal in mice, with ventricular septal and heart-valve defects. So the Cameron pathway belongs in the discovery tier, validated species by species, not stamped onto a first-generation production line.</p>
        </div>
      </RevealSection>

      <RevealSection delay={0.15}>
        <div className="grid md:grid-cols-2 gap-6 mb-4">
          <div className="bg-zinc-950 border border-amber-400 rounded-xl p-6">
            <div className="text-amber-400 text-xs uppercase tracking-wider mb-1">First Wave — validate first</div>
            <h4 className="text-white font-semibold mb-2">Stress + social buffering</h4>
            <div className="font-mono text-amber-300 text-sm mb-3">CRHR1 / CRHBP · FKBP5 · NPY · OXTR</div>
            <p className="text-zinc-400 text-sm">Lowest catastrophic risk. Cuts chronic fear, panic, and stress-axis overdrive while leaving protective nociception fully intact. Social buffering is species-specific: OXTR in mammals, mesotocin in birds, isotocin and vasotocin systems in fish.</p>
          </div>
          <div className="bg-zinc-950 border border-zinc-700 rounded-xl p-6">
            <div className="text-zinc-400 text-xs uppercase tracking-wider mb-1">Discovery Tier — gated, species-validated</div>
            <h4 className="text-white font-semibold mb-2">Pain-affect decoupling + positive-valence uplift</h4>
            <div className="font-mono text-zinc-300 text-sm mb-3">FAAH · SLC6A4 · BDNF · COMT · HTR2A · ZFHX2-downstream</div>
            <p className="text-zinc-400 text-sm">The abolition horizon, and the gradients-of-bliss ambition. Developed on species-validated targets and held behind the measurement gate before any animal carries it.</p>
          </div>
        </div>
        <p className="text-zinc-500 text-xs text-center mb-2">Validation order: prove the first wave, then expand. Nothing is generalized across species without species-specific validation.</p>
      </RevealSection>
    </div>
  </section>
);

const MeasurementLayer = () => {
  const stops = [
    'Less distress behavior but more lesions, burns, or lameness → line fails.',
    'Less activity with reduced feeding or poor growth → fails unless a non-pathological cause is proven.',
    'Less aggression with social withdrawal or maternal neglect → fails or is redesigned.',
    'Less pain behavior with impaired hazard avoidance or wound protection → fails.',
    'Better handling scores with signs of learned helplessness or extreme passivity → fails.',
  ];
  return (
    <section id="measurement" className="py-24 bg-zinc-950 relative">
      <div className="max-w-4xl mx-auto px-6">
        <RevealSection>
          <div className="text-center mb-12">
            <span className="text-amber-400 text-sm font-semibold tracking-widest uppercase mb-4 block">The Gate</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Proving It, Not Assuming It</h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">The hardest failure mode is false calm. A blunted, sick, injured, or helpless animal looks calmer, not worse, on every metric the industry already rewards. Behavior alone cannot tell genuine relief from suppressed expression. So no line reaches production until a measurement layer says it did what it claims.</p>
          </div>
        </RevealSection>
        <RevealSection delay={0.1}>
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-6">
              <h3 className="text-white font-bold mb-3">What it measures</h3>
              <ul className="space-y-2 text-zinc-400 text-sm">
                <li className="flex gap-2"><span className="text-amber-400">·</span>Behavior: vocalization, posture, gait, guarding, play, affiliative contact</li>
                <li className="flex gap-2"><span className="text-amber-400">·</span>Physiology: cortisol, heart-rate variability, temperature, respiration</li>
                <li className="flex gap-2"><span className="text-amber-400">·</span>Injury and lesions: wounds, lameness, infection surveillance</li>
                <li className="flex gap-2"><span className="text-amber-400">·</span>Social graph: proximity, aggression, isolation, maternal contact</li>
                <li className="flex gap-2"><span className="text-amber-400">·</span>Feeding: latency, competition, suppression under stress</li>
              </ul>
              <p className="text-zinc-500 text-xs mt-4">Output is a probabilistic welfare map, not a claim to read an animal's mind.</p>
            </div>
            <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-6">
              <h3 className="text-white font-bold mb-3">Stop rules</h3>
              <ul className="space-y-2 text-zinc-400 text-sm">
                {stops.map((s, i) => (<li key={i} className="flex gap-2"><span className="text-red-400">✕</span>{s}</li>))}
              </ul>
            </div>
          </div>
        </RevealSection>
        <RevealSection delay={0.15}>
          <div className="bg-zinc-950 border border-amber-400 rounded-2xl p-6">
            <p className="text-amber-400 text-center text-sm"><span className="text-white font-semibold">The enemy is blunting.</span> The only way to claim we abolished suffering, and later raised positive welfare, rather than just suppressed the animal's ability to express either, is to measure. The same rig that verifies the floor is what verifies the climb toward positive states. The gate is how the whole aim stays honest. A binding no-intensification clause comes with it: these genetics may never be used to justify worse conditions, higher density, or less care.</p>
          </div>
        </RevealSection>
      </div>
    </section>
  );
};

const Roadmap = () => (
  <section id="roadmap" className="py-24 bg-zinc-950 relative">
    <div className="max-w-3xl mx-auto px-6 text-center">
      <RevealSection>
        <span className="text-amber-400 text-sm font-semibold tracking-widest uppercase mb-4 block">Status</span>
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Where We Are</h2>
        <div className="w-24 h-1 bg-amber-400 mx-auto mb-8" />
        <p className="text-zinc-300 text-lg leading-relaxed mb-4">This initiative is at the earliest stage: pre-funding and pre-proof-of-concept. The candidate genetics have been identified from the published human and animal literature. None of it has been validated in livestock, and none of it is funded yet.</p>
        <p className="text-zinc-400 leading-relaxed">The first milestone is concrete and modest: assemble the industry alliance and fund first-wave validation of the stress-axis and social-buffering edits, with the measurement layer built alongside. Everything after that depends on what those results show. We would rather say that plainly than publish a timeline we cannot stand behind.</p>
      </RevealSection>
    </div>
  </section>
);

const ReturnCalculator = () => {
  const SPECIES_PRESETS = {
    broiler: { label: 'Broiler (Chicken)', throughput: 200, revenuePerAnimal: 3.50, feedCost: 0.35, animalWeight: 5.5, defectIncidence: 3, defectLoss: 0.80, defectReduction: 15, fcrPenalty: 3, mortalityReduction: 0.5, netMargin: 7, opexPct: 0.3, defectNote: 'Broiler PSE incidence ~2-5%; small per-bird loss', fcrNote: 'Literature: 2-5% stress-driven FCR penalty in broilers' },
    pork: { label: 'Pork (Swine)', throughput: 2000, revenuePerAnimal: 185, feedCost: 0.30, animalWeight: 265, defectIncidence: 18, defectLoss: 22, defectReduction: 20, fcrPenalty: 4, mortalityReduction: 0.8, netMargin: 8, opexPct: 0.5, defectNote: 'PSE incidence 10-30% in commercial pork; $15-30 per carcass loss', fcrNote: 'Literature: 3-8% stress-driven FCR penalty in swine' },
    beef: { label: 'Beef (Cattle)', throughput: 500, revenuePerAnimal: 1400, feedCost: 0.28, animalWeight: 1350, defectIncidence: 5, defectLoss: 120, defectReduction: 20, fcrPenalty: 5, mortalityReduction: 0.3, netMargin: 5, opexPct: 0.3, defectNote: 'DFD incidence ~3-8% in beef; larger per-carcass penalty than PSE', fcrNote: 'Literature: 3-8% stress-driven FCR penalty in feedlot cattle' },
  };
  const [species, setSpecies] = useState('broiler');
  const preset = SPECIES_PRESETS[species];
  const [annualThroughput, setAnnualThroughput] = useState(preset.throughput);
  const [defectIncidence, setDefectIncidence] = useState(preset.defectIncidence);
  const [defectLoss, setDefectLoss] = useState(preset.defectLoss);
  const [defectReduction, setDefectReduction] = useState(preset.defectReduction);
  const [fcrPenalty, setFcrPenalty] = useState(preset.fcrPenalty);
  const [feedCost, setFeedCost] = useState(preset.feedCost);
  const [animalWeight, setAnimalWeight] = useState(preset.animalWeight);
  const [mortalityReduction, setMortalityReduction] = useState(preset.mortalityReduction);
  const [netMargin, setNetMargin] = useState(preset.netMargin);
  const [revenuePerAnimal, setRevenuePerAnimal] = useState(preset.revenuePerAnimal);
  const [opexPct, setOpexPct] = useState(preset.opexPct);
  const applyPreset = (s) => {
    const p = SPECIES_PRESETS[s];
    setSpecies(s); setAnnualThroughput(p.throughput); setDefectIncidence(p.defectIncidence); setDefectLoss(p.defectLoss);
    setDefectReduction(p.defectReduction); setFcrPenalty(p.fcrPenalty); setFeedCost(p.feedCost); setAnimalWeight(p.animalWeight);
    setMortalityReduction(p.mortalityReduction); setNetMargin(p.netMargin); setRevenuePerAnimal(p.revenuePerAnimal); setOpexPct(p.opexPct);
  };
  const isBroiler = species === 'broiler';
  const throughputActual = isBroiler ? annualThroughput * 1000000 : annualThroughput * 1000;
  const annualRevenue = throughputActual * revenuePerAnimal;
  const defectSavings = (defectIncidence / 100) * (defectReduction / 100) * defectLoss * throughputActual;
  const fcrSavings = (fcrPenalty / 100) * feedCost * animalWeight * throughputActual;
  const mortalitySavings = (mortalityReduction / 100) * revenuePerAnimal * (netMargin / 100) * throughputActual;
  const opexSavings = annualRevenue * (opexPct / 100);
  const totalSavings = defectSavings + fcrSavings + mortalitySavings + opexSavings;
  const revenueImpactPct = annualRevenue > 0 ? ((totalSavings / annualRevenue) * 100).toFixed(2) : 0;
  const fmt = (n) => {
    if (n >= 1000000000) return '$' + (n / 1000000000).toFixed(1) + 'B';
    if (n >= 1000000) return '$' + (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return '$' + (n / 1000).toFixed(0) + 'K';
    return '$' + n.toFixed(0);
  };
  const fmtRevenue = (n) => {
    if (n >= 1000000000) return '$' + (n / 1000000000).toFixed(1) + 'B';
    return '$' + (n / 1000000).toFixed(0) + 'M';
  };
  const allianceFee = annualRevenue >= 10000000000 ? 1000000 : annualRevenue >= 1000000000 ? 250000 : annualRevenue >= 100000000 ? 100000 : annualRevenue >= 10000000 ? 50000 : 25000;
  const allianceTier = annualRevenue >= 10000000000 ? 'Over $10B revenue' : annualRevenue >= 1000000000 ? '$1B–$10B revenue' : annualRevenue >= 100000000 ? '$100M–$1B revenue' : annualRevenue >= 10000000 ? '$10M–$100M revenue' : 'Under $10M revenue';
  const throughputLabel = isBroiler ? 'Annual throughput (millions of birds)' : 'Annual throughput (thousands of animals)';
  const throughputMax = isBroiler ? 2000 : species === 'beef' ? 5000 : 10000;
  const throughputStep = isBroiler ? 10 : species === 'beef' ? 10 : 100;
  const SliderInput = ({ label, value, setValue, min, max, step, format, sublabel }) => (
    <div className="mb-5">
      <div className="flex justify-between items-center mb-1">
        <span className="text-zinc-400 text-xs">{label}</span>
        <span className="text-amber-400 text-xs font-mono font-semibold">{format ? format(value) : value}</span>
      </div>
      {sublabel && <p className="text-zinc-600 text-xs mb-1 italic">{sublabel}</p>}
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => setValue(parseFloat(e.target.value))} className="w-full h-1 bg-zinc-700 rounded-full appearance-none cursor-pointer accent-amber-400" />
      <div className="flex justify-between text-zinc-500 text-xs mt-1">
        <span>{format ? format(min) : min}</span><span>{format ? format(max) : max}</span>
      </div>
    </div>
  );
  return (
    <div className="bg-zinc-950 border border-amber-400 rounded-2xl p-8 mb-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="text-white font-bold text-lg mb-1">Model the Return</h3>
          <p className="text-zinc-500 text-sm">Adjust inputs to match your operation.</p>
        </div>
        <div className="flex gap-2">
          {Object.entries(SPECIES_PRESETS).map(([key, p]) => (
            <button key={key} onClick={() => applyPreset(key)} className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all ${species === key ? 'bg-amber-400 text-zinc-950' : 'bg-zinc-800 text-zinc-400 hover:text-white border border-zinc-700'}`}>{p.label}</button>
          ))}
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-8 mb-8">
        <div>
          <h4 className="text-zinc-300 font-semibold text-sm mb-4 pb-2 border-b border-zinc-800">Operation Scale</h4>
          <SliderInput label={throughputLabel} value={annualThroughput} setValue={setAnnualThroughput} min={10} max={throughputMax} step={throughputStep} format={(v) => v.toLocaleString() + (isBroiler ? 'M' : 'K')} />
          <div className="bg-zinc-900 rounded-lg px-4 py-2 mb-4 text-xs text-zinc-500 flex justify-between">
            <span>Est. annual revenue</span><span className="text-zinc-300 font-semibold">{fmtRevenue(annualRevenue)}</span>
          </div>
          <SliderInput label="Revenue per animal" value={revenuePerAnimal} setValue={setRevenuePerAnimal} min={species === 'broiler' ? 1 : species === 'pork' ? 50 : 400} max={species === 'broiler' ? 8 : species === 'pork' ? 400 : 2500} step={species === 'broiler' ? 0.1 : species === 'pork' ? 5 : 50} format={(v) => '$' + (v >= 1000 ? (v/1000).toFixed(1)+'K' : v.toFixed(species === 'broiler' ? 2 : 0))} />
          <SliderInput label="Feed cost per lb" value={feedCost} setValue={setFeedCost} min={0.15} max={0.55} step={0.01} format={(v) => '$' + v.toFixed(2)} sublabel="Cost per lb of liveweight produced, not raw feed price." />
          <SliderInput label="Average live weight (lbs)" value={animalWeight} setValue={setAnimalWeight} min={species === 'broiler' ? 3 : species === 'pork' ? 180 : 900} max={species === 'broiler' ? 9 : species === 'pork' ? 350 : 1600} step={species === 'broiler' ? 0.5 : species === 'pork' ? 5 : 25} format={(v) => v + ' lbs'} />
        </div>
        <div>
          <h4 className="text-zinc-300 font-semibold text-sm mb-4 pb-2 border-b border-zinc-800">Stress-Linked Loss Assumptions</h4>
          <SliderInput label="Carcass defect incidence" value={defectIncidence} setValue={setDefectIncidence} min={1} max={species === 'pork' ? 35 : 15} step={0.5} format={(v) => v + '%'} sublabel={preset.defectNote} />
          <SliderInput label="Per-carcass loss from defects" value={defectLoss} setValue={setDefectLoss} min={species === 'broiler' ? 0.2 : species === 'pork' ? 5 : 30} max={species === 'broiler' ? 3 : species === 'pork' ? 45 : 250} step={species === 'broiler' ? 0.1 : species === 'pork' ? 1 : 5} format={(v) => '$' + v.toFixed(species === 'broiler' ? 2 : 0)} />
          <SliderInput label="Expected defect reduction from first-wave edits" value={defectReduction} setValue={setDefectReduction} min={10} max={70} step={5} format={(v) => v + '%'} sublabel="Speculative — no livestock validation yet. Default is conservative." />
          <SliderInput label="Stress-driven FCR penalty" value={fcrPenalty} setValue={setFcrPenalty} min={1} max={10} step={0.5} format={(v) => v + '%'} sublabel={preset.fcrNote} />
          <SliderInput label="Mortality reduction (percentage points)" value={mortalityReduction} setValue={setMortalityReduction} min={0.1} max={3} step={0.1} format={(v) => v.toFixed(1) + 'pp'} />
          <SliderInput label="Net margin per animal" value={netMargin} setValue={setNetMargin} min={2} max={20} step={1} format={(v) => v + '%'} sublabel="Recovered animals are valued at margin, not full revenue, since a surviving animal still costs feed and processing." />
        </div>
      </div>
      <div className="bg-zinc-900 rounded-xl p-6 mb-4">
        <h4 className="text-zinc-400 text-xs font-semibold uppercase tracking-wider mb-4">Estimated Annual Margin Impact</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="text-center"><div className="text-green-400 font-bold text-lg">{fmt(defectSavings)}</div><div className="text-zinc-400 text-xs mt-1">Carcass defect reduction</div></div>
          <div className="text-center"><div className="text-green-400 font-bold text-lg">{fmt(fcrSavings)}</div><div className="text-zinc-400 text-xs mt-1">Feed conversion improvement</div></div>
          <div className="text-center"><div className="text-green-400 font-bold text-lg">{fmt(mortalitySavings)}</div><div className="text-zinc-400 text-xs mt-1">Mortality reduction (at margin)</div></div>
          <div className="text-center"><div className="text-green-400 font-bold text-lg">{fmt(opexSavings)}</div><div className="text-zinc-400 text-xs mt-1">Stress-management OpEx</div></div>
        </div>
        <div className="border-t border-zinc-700 pt-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <div className="flex items-baseline gap-3 flex-wrap">
              <div className="text-white text-2xl font-bold">{fmt(totalSavings)}<span className="text-zinc-500 text-base font-normal">/year</span></div>
              <div className="text-green-400 text-2xl font-bold">{revenueImpactPct}%<span className="text-zinc-500 text-base font-normal"> of annual revenue</span></div>
            </div>
            <div className="text-zinc-500 text-xs mt-1">Estimated annual margin impact</div>
          </div>
          <div className="text-right">
            <div className="text-zinc-400 text-xs">Alliance fee at this scale: <span className="text-amber-400 font-semibold">{fmt(allianceFee)}/year</span></div>
            <div className="text-zinc-600 text-xs mt-1">{allianceTier}</div>
          </div>
        </div>
      </div>
      <p className="text-zinc-500 text-xs text-center">Illustrative estimates based on published production literature. Actual results depend on species mix, operation type, and edit stack validation outcomes.</p>
    </div>
  );
};

const EconomicCase = () => (
  <section id="economics" className="py-24 bg-zinc-900 relative">
    <div className="max-w-5xl mx-auto px-6">
      <RevealSection>
        <div className="text-center mb-12">
          <span className="text-amber-400 text-sm font-semibold tracking-widest uppercase mb-4 block">For Industry</span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">The Economic Case</h2>
          <p className="text-zinc-400 max-w-2xl mx-auto">A production-economics analysis for livestock producers and integrators.</p>
        </div>
      </RevealSection>
      <RevealSection delay={0.05}>
        <div className="bg-zinc-950 border border-zinc-800 rounded-2xl p-8 mb-8">
          <p className="text-zinc-200 leading-relaxed mb-4">The comparison class for this program is conventional genetic improvement at PIC, Genus, Aviagen, Cobb, and Hendrix. The same breeding pyramid that drove a 40% FCR improvement in broilers over 70 years. We are proposing a new axis of improvement targeting a category of margin loss the industry already tracks: stress-linked production loss.</p>
          <p className="text-zinc-200 leading-relaxed mb-4">The first-wave edits target chronic fear and stress directly. Animals that do not experience them do not chronically elevate cortisol. The economic benefits flow from that downstream effect: improved feed conversion, lower mortality, and reduced carcass defects.</p>
          <p className="text-zinc-200 leading-relaxed">No consumer premium required. No behavior change required. The return is captured inside the existing commodity grading and pricing system, at current prices, on current volume.</p>
        </div>
      </RevealSection>
      <RevealSection delay={0.08}>
        <h3 className="text-white font-bold text-xl mb-6">Four Margin Levers</h3>
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-6">
            <div className="text-amber-400 font-semibold mb-1">01: Stress-Linked Carcass Defects</div>
            <div className="text-zinc-200 text-xs font-semibold mb-3">PSE in pork: 10–30% incidence. DFD in beef: lower incidence, larger penalty.</div>
            <p className="text-zinc-300 text-sm">Acute pre-slaughter stress accelerates post-mortem glycolysis in pigs (PSE) and depletes glycogen in cattle (DFD). Both are graded against and tracked industry-wide. Eliminating the acute stress response reduces incidence directly.</p>
          </div>
          <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-6">
            <div className="text-amber-400 font-semibold mb-1">02: Feed Conversion</div>
            <div className="text-zinc-200 text-xs font-semibold mb-3">Stress-driven FCR penalty: 2–8%. At Tyson scale: $120–300M/year.</div>
            <p className="text-zinc-300 text-sm">Cortisol is catabolic. It mobilizes glucose, breaks down muscle protein, and diverts energy from growth to maintenance. The stress-attributable portion of that drag is what genetic edits can capture.</p>
          </div>
          <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-6">
            <div className="text-amber-400 font-semibold mb-1">03: Mortality</div>
            <div className="text-zinc-200 text-xs font-semibold mb-3">Broiler mortality: 3–5%. Pig mortality: 5–10%. Significant portions stress-linked.</div>
            <p className="text-zinc-300 text-sm">Chronic cortisol suppresses immune function. A 1-point mortality reduction at 2B-bird scale is 20M additional marketable birds per year. Reduced antibiotic use aligns with retailer programs already in force.</p>
          </div>
          <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-6">
            <div className="text-amber-400 font-semibold mb-1">04: Stress-Management OpEx</div>
            <div className="text-zinc-200 text-xs font-semibold mb-3">Recurring labor and capex that exists solely to manage stress-driven pathologies.</div>
            <p className="text-zinc-300 text-sm">Tail docking, beak trimming, CO2 stunning systems, specialized lairage, enrichment compliance. Animals without the underlying stress pathology don't develop the behaviors that require these interventions.</p>
          </div>
        </div>
      </RevealSection>
      <RevealSection delay={0.16}>
        <ReturnCalculator />
      </RevealSection>
      <RevealSection delay={0.18}>
        <div className="bg-zinc-950 border border-amber-400 rounded-2xl p-6 mb-8">
          <p className="text-amber-400 text-center text-sm">Truly effective altruism should not depend on changes in consumer behavior or expect corporations to act against their economic incentives. <span className="text-white font-semibold">This approach does neither.</span></p>
        </div>
      </RevealSection>
    </div>
  </section>
);


const IndustryAlliance = () => {
  const [formData, setFormData] = useState({ orgName: '', contactName: '', email: '', orgType: '', annualRevenue: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const handleSubmit = async () => {
    if (!formData.email || !formData.orgName) return;
    await submitToFormspree(FORMSPREE_CONFIG.allianceForm, { ...formData, type: 'alliance_application', timestamp: new Date().toISOString() });
    setSubmitted(true);
  };
  return (
    <section id="alliance" className="py-24 bg-zinc-900 relative">
      <div className="max-w-5xl mx-auto px-6">
        <RevealSection>
          <div className="text-center mb-12">
            <span className="text-amber-400 text-sm font-semibold tracking-widest uppercase mb-4 block">Coalition</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Industry Alliance</h2>
            <p className="text-zinc-400 max-w-2xl mx-auto mb-4">Pre-competitive collaboration to fund the science, navigate regulatory approval, and prepare markets for adoption.</p>
          </div>
        </RevealSection>
        <RevealSection delay={0.05}>
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <div className="text-amber-400 text-2xl mb-3">01</div>
              <h3 className="text-white font-bold text-lg mb-2">Fund the Science</h3>
              <p className="text-zinc-400 text-sm">Pool resources for proof-of-concept research. Shared R&D cost, universal access to results.</p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <div className="text-amber-400 text-2xl mb-3">02</div>
              <h3 className="text-white font-bold text-lg mb-2">Clear the Regulators</h3>
              <p className="text-zinc-400 text-sm">Coordinate regulatory strategy across jurisdictions. FDA has approved gene-edited livestock (Genus PRRS-resistant pig, 2025).</p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <div className="text-amber-400 text-2xl mb-3">03</div>
              <h3 className="text-white font-bold text-lg mb-2">Scale Adoption</h3>
              <p className="text-zinc-400 text-sm">Prepare markets for zero-suffering genetics. Coordinate messaging, develop certification standards.</p>
            </div>
          </div>
        </RevealSection>
        <RevealSection delay={0.15}>
          <div className="bg-zinc-900 border border-amber-400 rounded-2xl p-8 mb-8">
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold text-white mb-2">Proposed Membership Fees</h3>
              <p className="text-zinc-500 text-sm">Fees scale to company size. Every member has skin in the game.</p>
            </div>
            <div className="overflow-x-auto mb-6">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-700">
                    <th className="text-left text-zinc-400 font-medium py-3 px-4">Annual Revenue</th>
                    <th className="text-right text-zinc-400 font-medium py-3 px-4">Annual Membership Fee</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-zinc-800"><td className="text-zinc-300 py-3 px-4">Over $10B</td><td className="text-amber-400 font-semibold py-3 px-4 text-right">$1,000,000</td></tr>
                  <tr className="border-b border-zinc-800"><td className="text-zinc-300 py-3 px-4">$1B – $10B</td><td className="text-amber-400 font-semibold py-3 px-4 text-right">$250,000</td></tr>
                  <tr className="border-b border-zinc-800"><td className="text-zinc-300 py-3 px-4">$100M – $1B</td><td className="text-amber-400 font-semibold py-3 px-4 text-right">$100,000</td></tr>
                  <tr className="border-b border-zinc-800"><td className="text-zinc-300 py-3 px-4">$10M – $100M</td><td className="text-amber-400 font-semibold py-3 px-4 text-right">$50,000</td></tr>
                  <tr><td className="text-zinc-300 py-3 px-4">Under $10M</td><td className="text-amber-400 font-semibold py-3 px-4 text-right">$25,000</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </RevealSection>
        <RevealSection delay={0.25}>
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8">
            {submitted ? (
              <div className="text-center py-8">
                <div className="text-5xl mb-4">✓</div>
                <h3 className="text-2xl font-bold text-white mb-4">Application Received</h3>
                <p className="text-zinc-400">We'll review your application and follow up within 5 business days.</p>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-white mb-2">Apply to Join Industry Alliance</h3>
                  <p className="text-zinc-500 text-sm">Founding members will shape the research agenda and governance structure.</p>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <input type="text" placeholder="Organization Name" value={formData.orgName} onChange={(e) => setFormData({...formData, orgName: e.target.value})} className="bg-zinc-950 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:border-amber-400 focus:outline-none" />
                  <input type="text" placeholder="Contact Name" value={formData.contactName} onChange={(e) => setFormData({...formData, contactName: e.target.value})} className="bg-zinc-950 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:border-amber-400 focus:outline-none" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <input type="email" placeholder="Email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="bg-zinc-950 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:border-amber-400 focus:outline-none" />
                  <select value={formData.orgType} onChange={(e) => setFormData({...formData, orgType: e.target.value})} className="bg-zinc-950 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:border-amber-400 focus:outline-none">
                    <option value="">Organization Type</option>
                    <option value="producer">Producer / Farm Operation</option>
                    <option value="processor">Processor / Distributor</option>
                    <option value="genetics">Genetics / Biotech Company</option>
                    <option value="retailer">Retailer / Food Service</option>
                    <option value="investor">Investor / Funder</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <textarea placeholder="Why do you want to join the alliance? (optional)" rows={3} value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} className="w-full bg-zinc-950 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:border-amber-400 focus:outline-none resize-none" />
                <button onClick={handleSubmit} className="w-full py-4 bg-amber-400 text-zinc-950 font-semibold rounded-lg hover:bg-amber-300 transition-all">Submit Application</button>
              </div>
            )}
          </div>
        </RevealSection>
      </div>
    </section>
  );
};


const Footer = ({ onNavigate, onHome }) => {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const handleSubscribe = async () => {
    if (!email) return;
    await submitToFormspree(FORMSPREE_CONFIG.newsletterForm, { email, type: 'newsletter', timestamp: new Date().toISOString() });
    setSubscribed(true);
  };
  return (
    <footer className="py-16 bg-zinc-950 border-t border-zinc-800">
      <div className="max-w-6xl mx-auto px-6">
        <div className="mb-12 pb-12 border-b border-zinc-800">
          <div className="max-w-md mx-auto text-center">
            <h3 className="text-white font-semibold mb-2">Stay Updated</h3>
            <p className="text-zinc-500 text-sm mb-4">Get notified when we hit milestones.</p>
            {subscribed ? (
              <p className="text-green-400 text-sm">✓ Subscribed! We'll be in touch.</p>
            ) : (
              <div className="flex gap-2">
                <input type="email" placeholder="your@email.com" value={email} onChange={(e) => setEmail(e.target.value)} className="flex-1 bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-2 text-white text-sm focus:border-amber-400 focus:outline-none" />
                <button onClick={handleSubscribe} className="px-4 py-2 bg-amber-400 text-zinc-950 font-medium rounded-lg hover:bg-amber-300 transition-all text-sm">Subscribe</button>
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col items-center py-6">
          <div className="text-zinc-500 text-sm flex flex-col items-center gap-5">
            <a href="https://x.com/0looshorg" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300">@0looshorg</a>
            <span>contact@0loosh.org</span>
          </div>
        </div>
        <div className="mt-12 pt-8 border-t border-zinc-800 text-center">
          <Logo size="sm" variant="symbol" />
          <p className="text-zinc-700 text-xs mt-2">© 2026 0loosh.org</p>
        </div>
      </div>
    </footer>
  );
};

export default function FactoryFarms({ onNavigate: onRoute }) {
  const [activeSection, setActiveSection] = useState('home');
  const handleNavigate = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };
  const goHome = () => {
    if (onRoute) onRoute('/');
    else window.location.href = '/';
  };
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['home', 'about', 'science', 'economics', 'roadmap', 'alliance'];
      const scrollPos = window.scrollY + window.innerHeight / 3;
      for (const section of sections) {
        const el = document.getElementById(section);
        if (el && scrollPos >= el.offsetTop && scrollPos < el.offsetTop + el.offsetHeight) {
          setActiveSection(section);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return (
    <div className="bg-zinc-950 text-white min-h-screen overflow-x-hidden">
      <Navigation activeSection={activeSection} onNavigate={handleNavigate} onHome={goHome} />
      <Hero onNavigate={handleNavigate} />
      <About />
      <Science />
      <MeasurementLayer />
      <EconomicCase />
      <Roadmap />
      <IndustryAlliance />
      <Footer onNavigate={handleNavigate} onHome={goHome} />
    </div>
  );
}
